import { useContext, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { format, isValid } from 'date-fns';

import { Flex } from '@zillow/constellation';
import { Box, Button, FormField, FormHelp, Input, Label } from '@zillow/constellation-10';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../../components/Wizard/context';

import { AppSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';

import { useForm } from '../../../../hooks';

import actions from '../../../../containers/CreateListingPage/actions';
import wizardActions from '../../../../components/Wizard/actions';
import PropertiesActions from '../../../../actions/PropertiesActions';
import PostingActions from '../../../../actions/PostingActions';

import { postingConstraints } from '../../../../utils/formValidation';

import { EMAIL_VERIFICATION_CONFIG } from '../../../Modal/VerificationModal/constants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';

import ContactEmailDeliverability from '../../../../model/ContactEmailDeliverability';

import { VerificationModal } from '../../../Modal/VerificationModal';
import ContactEmailDeliverabilityAlert from '../../../../components/ContactEmailDeliverabilityAlert';
import ContactEmailMismatchAlert from '../../../../components/ContactEmailMismatchAlert';
import WizardAlert from '../../../../components/WizardAlert';

import type { ChangeEvent } from 'react';
import type { ContactEmailDeliverabilityProps } from '../../../../model/ContactEmailDeliverability';
import type { MockEventProps } from './interfaces';
import type { ErrorRMWProps } from '../../../../model/ErrorRMW';

const inputName = 'contactEmail';

type Props = {
  onClose?: () => void;
};

const EmailFormField = ({ onClose }: Props): JSX.Element => {
  const dispatch = useDispatch();

  const { reducerWizardDispatch } = useContext(WizardContext);

  const {
    user: {
      features: { canViewContactEmailDeliverability },
    },
    currentProperty: { listingAlias, contactEmail, dateEmailVerified },
  } = useSelector(
    (state) => ({ app: state.app, user: state.user, currentProperty: state.properties.currentProperty }),
    shallowEqual,
  );
  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));

  const [isFocused, setIsFocused] = useState(false);
  const [displayEmailVerificationModal, setDisplayEmailVerificationModal] = useState(Boolean(onClose));

  const {
    modularListingState: { isInModalState, isSaveComplete, serverErrorObj },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const {
    inputs,
    errorObj,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      contactEmail,
    },
    postingConstraints,
  );

  const memoizedEmailVericationButton = useMemo(
    () => (
      <Button tone="neutral" emphasis="outlined" onClick={() => setDisplayEmailVerificationModal(true)}>
        {contactEmail ? 'Edit' : 'Add email address'}
      </Button>
    ),
    [contactEmail],
  );

  useEffect(() => {
    if (isSaveComplete) {
      save({
        target: {
          name: inputName,
          // @ts-expect-error
          value: inputs.contactEmail,
        },
      });
    }
  }, [isSaveComplete]);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event: ChangeEvent<HTMLInputElement> | MockEventProps): Promise<void> => {
    const { value, name: fieldName } = event.target;

    if (hasFieldError(fieldName, value)) {
      return Promise.resolve();
    }

    const dataToSave = {
      fieldName,
      value,
      propertyId: listingAlias,
    };

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    return (
      dispatch(PostingActions.property.edit(dataToSave, { reserveErrorObject: true }))
        // @ts-expect-error
        .then(({ response }: { response: ContactEmailDeliverabilityProps }) => {
          dispatch(
            PropertiesActions.updateCurrentProperty({
              [fieldName]: value,
              contactEmailDeliverability: ContactEmailDeliverability(response),
            }),
          );
          dispatch(PropertiesActions.updateCurrentPropertyDateEmailVerified(response?.dateEmailVerified));

          // reset save cta
          actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
        })
        .catch((err: string | ErrorRMWProps) => {
          throw err;
        })
        .finally(() => {
          wizardActions.updateIsFetchingData({
            reducerWizardDispatch,
            isFetchingData: false,
          });

          setIsFocused(false);
        })
    );
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement> | MockEventProps) => {
    clearServerError();
    // @ts-expect-error
    handleFormChange(event);
  };

  const handleEmailAddedOrVerificationConfirmation = (newContactEmail: string): Promise<void> => {
    return save({
      target: {
        name: inputName,
        value: newContactEmail,
      },
    });
  };

  return (
    <>
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      <FormField
        data-testid="EmailFormField"
        label={<Label required={true}>Email</Label>}
        // @ts-expect-error
        error={!!errorObj.contactEmail}
        control={
          <>
            {inputs.contactEmail ? (
              <Flex display="flex" flexDirection="row" gap="sm">
                <Input
                  fluid
                  disabled={true}
                  size={isScreenDesktop ? 'lg' : 'md'}
                  name="contactEmail"
                  value={String(inputs.contactEmail || '')}
                  onChange={handleChange}
                  // @ts-expect-error
                  error={!!errorObj.contactEmail}
                  aria-describedby={dateEmailVerified ? 'verification' : undefined}
                  className="fs-mask"
                  css={{
                    '&[disabled]': {
                      opacity: 1,
                    },
                  }}
                />
                {memoizedEmailVericationButton}
              </Flex>
            ) : (
              <>{memoizedEmailVericationButton}</>
            )}
          </>
        }
        description={
          <>
            {/* @ts-expect-error */}
            {errorObj.contactEmail ? (
              // @ts-expect-error
              <FormHelp error={!!errorObj.contactEmail}>{errorObj.contactEmail}</FormHelp>
            ) : (
              <>
                {Boolean(inputs.contactEmail) && (
                  <FormHelp id="verification">
                    {isValid(new Date(dateEmailVerified))
                      ? `Verified on ${format(new Date(dateEmailVerified), 'MMMM d, yyyy')}`
                      : 'Previously verified'}
                  </FormHelp>
                )}

                {!isInModalState && !isFocused && inputs.contactEmail && (
                  <Box css={{ marginBlock: '400' }}>
                    {/* @ts-expect-error */}
                    <ContactEmailMismatchAlert contactEmail={inputs.contactEmail} />
                  </Box>
                )}
              </>
            )}

            {canViewContactEmailDeliverability && !isInModalState && (
              <Box css={{ marginBlock: '400' }}>
                <ContactEmailDeliverabilityAlert />
              </Box>
            )}
          </>
        }
      />

      {displayEmailVerificationModal && (
        <VerificationModal
          headerText="Add and verify email address"
          onContentValueAdded={handleEmailAddedOrVerificationConfirmation}
          onVerificationConfirmation={handleEmailAddedOrVerificationConfirmation}
          showVerificationModal={(showValue) => {
            onClose?.();
            setDisplayEmailVerificationModal(showValue);
          }}
          stepsConfig={EMAIL_VERIFICATION_CONFIG}
          verificationMode="email"
        />
      )}
    </>
  );
};

export default EmailFormField;
